"use client";

import { ServerActionName } from "@/src/actions/enums";
import { BackendImportTasksOutput, BackendSearchImportTaskInput, BackendSearchImportTaskOutput } from "@/src/types/api/smartUpload";
import { useApiServerAction } from "@/src/hooks/useServerAction";
import { useCallback, useEffect, useState } from "react";
import { useNotificationContext } from "@/src/pattern-library/notification/NotificationProvider";
import { useFeatureFlag } from "@/src/hooks/useFeatureFlag";
import { useInterval } from "@/src/hooks/useInterval";
import { SortOrder } from "@/src/types/common";
import { ImportStatusEnum } from "@/src/enums/smartImport";
import { Namespace } from "@/src/i18n/namespace";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { NON_AUTHENTICATED_PREFIXES, supplierRoutes } from "@/src/constants/routes";
import { Button } from "@/src/pattern-library/Button";
const forbiddenPollRoutePrefixes = [supplierRoutes.smartUpload.index, ...NON_AUTHENTICATED_PREFIXES];
export function useSmartImportTaskPolling() {
  const getSmartImportStatus = useApiServerAction<BackendSearchImportTaskInput, BackendSearchImportTaskOutput>(ServerActionName.SearchImportTaskStatus);
  const tImport = useTranslations(Namespace.SMART_UPLOAD);
  const {
    notification
  } = useNotificationContext();
  const [shouldPoll, setShouldPoll] = useState(false);
  const {
    isSmartUploadTaskNotificationEnabled
  } = useFeatureFlag();
  const pathname = usePathname();
  const [cachedLatestTask, setCachedLatestTask] = useState<BackendImportTasksOutput | undefined>(undefined);
  const onTaskStatusChange = (task: BackendImportTasksOutput, status?: ImportStatusEnum) => {
    switch (status) {
      case ImportStatusEnum.FINISHED:
        {
          notification.success({
            message: tImport("toast.importSuccess", {
              taskId: task?.fileKey
            }),
            btn: <Button type="link" onClick={() => {
              window.open(supplierRoutes.smartUpload.index, "_blank");
              notification.destroy();
            }}>
              {tImport("button.viewSummary")}
            </Button>,
            duration: null
          });
          break;
        }
      default:
        break;
    }
  };
  const intervalCallback = async () => {
    try {
      // Fetch most recent smart import task
      const {
        data,
        errorCode
      } = await getSmartImportStatus({
        sort: {
          field: "createdAt",
          order: SortOrder.DESC
        },
        pagination: {
          limit: 1,
          offset: 0
        }
      });
      if (errorCode || !data) {
        throw new Error("Failed to fetch smart import status");
      }
      const latestTask = data?.result?.[0];
      const updatedSmartImportStatus = latestTask.status;
      const shouldContinuePolling = updatedSmartImportStatus === ImportStatusEnum.IN_PROGRESS;
      if (latestTask.id !== cachedLatestTask?.id) {
        // Cached latest task is invalid, update with new latest task.
        setCachedLatestTask(latestTask);

        // Continue polling if new task is in progress.
      } else if (updatedSmartImportStatus !== cachedLatestTask?.status) {
        // Still polling the same task, update states if status has changed.
        setCachedLatestTask(latestTask);
        onTaskStatusChange(latestTask, updatedSmartImportStatus);
      }
      if (!shouldContinuePolling) {
        setShouldPoll(false);
      }
    } catch (error) {
      console.error("Error polling smart import task", error);
      setShouldPoll(false);
    }
  };
  const pollSmartImportTask = useCallback(() => {
    setShouldPoll(true);
  }, []);
  useInterval({
    shouldPoll,
    delay: 10 * 1000,
    // 10 seconds
    timeout: 1 * 60 * 1000,
    // 10 minutes
    callback: intervalCallback
  });
  useEffect(() => {
    const isForbiddenPollRoute = forbiddenPollRoutePrefixes.some(prefix => pathname.includes(prefix));
    if (!isForbiddenPollRoute && isSmartUploadTaskNotificationEnabled) {
      setShouldPoll(true);
    }
  }, [pathname]);
  return {
    pollSmartImportTask,
    setCachedLatestTask
  };
}